import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import './YTThumbnail.css';

const YTThumbnail = () => {
    const [videoUrl, setVideoUrl] = useState('');
    const [thumbnailUrl, setThumbnailUrl] = useState('');
    const [error, setError] = useState('');

    const getVideoId = (url) => {
        const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([\w-]+)/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const videoId = getVideoId(videoUrl);
        
        if (videoId) {
            setError('');
            setThumbnailUrl(`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`);
        } else {
            setError('Invalid YouTube URL');
            setThumbnailUrl('');
        }
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = thumbnailUrl;
        link.setAttribute('download', 'thumbnail.jpg');
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        
        window.open(thumbnailUrl, '_blank');
    };

    // Schema.org structured data
    const webApplicationSchema = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "YouTube Thumbnail Downloader",
        "description": "Free online tool to download high-quality YouTube video thumbnails instantly. Extract HD thumbnails from any YouTube video without registration.",
        "url": "https://ytcopycat.com/youtube_thumbnail_downloader",
        "applicationCategory": "MultimediaApplication",
        "operatingSystem": "Web Browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "creator": {
            "@type": "Organization",
            "name": "YT Copycat"
        },
        "featureList": [
            "Download YouTube thumbnails in HD quality",
            "Extract thumbnails from YouTube videos",
            "Free thumbnail downloader",
            "No registration required",
            "Instant thumbnail preview",
            "High resolution thumbnail images"
        ]
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "How to download YouTube thumbnail images?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Simply paste the YouTube video URL into our thumbnail downloader tool, click 'Get Thumbnail', and then download the high-quality thumbnail image instantly."
                }
            },
            {
                "@type": "Question",
                "name": "What quality are the downloaded YouTube thumbnails?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our tool downloads YouTube thumbnails in maximum resolution (maxresdefault.jpg) which provides the highest quality available for each video."
                }
            },
            {
                "@type": "Question",
                "name": "Is this YouTube thumbnail extractor free to use?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, our YouTube thumbnail downloader is completely free to use with no registration required and no limits on downloads."
                }
            },
            {
                "@type": "Question",
                "name": "What YouTube URL formats are supported?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Our tool supports all YouTube URL formats including youtube.com/watch, youtu.be short links, and embedded URLs."
                }
            }
        ]
    };

    return (
        <div className="yt-thumbnail-page">
            <Helmet>
                <title>Free YouTube Thumbnail Downloader - Download HD Video Thumbnails | YT Copycat</title>
                <meta name="description" content="Download YouTube thumbnails in HD quality for free. Best YouTube thumbnail extractor and downloader tool. Get high-resolution thumbnail images from any YouTube video instantly without registration." />
                <meta name="keywords" content="youtube thumbnail downloader, download youtube thumbnail, youtube thumbnail extractor, youtube thumbnail hd, youtube thumbnail high quality, youtube thumbnail download free, extract youtube thumbnail, youtube video thumbnail, youtube thumbnail image download, youtube thumbnail grabber, youtube thumbnail saver, download video thumbnail, youtube thumbnail tool online, youtube thumbnail downloader free online, youtube thumbnail download hd, youtube thumbnail png, youtube thumbnail jpg, youtube video image download, youtube cover image download, youtube preview image download" />
                
                {/* Open Graph tags */}
                <meta property="og:title" content="Free YouTube Thumbnail Downloader - Download HD Video Thumbnails" />
                <meta property="og:description" content="Extract and download high-quality thumbnails from any YouTube video. Free, fast, and no registration required. Best YouTube thumbnail downloader tool online." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://ytcopycat.com/youtube_thumbnail_downloader" />
                <meta property="og:image" content="https://ytcopycat.com/images/youtube-thumbnail-downloader-og.jpg" />
                <meta property="og:site_name" content="YT Copycat" />
                
                {/* Twitter Card tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Free YouTube Thumbnail Downloader - Download HD Video Thumbnails" />
                <meta name="twitter:description" content="Extract and download high-quality thumbnails from any YouTube video. Free, fast, and no registration required." />
                <meta name="twitter:image" content="https://ytcopycat.com/images/youtube-thumbnail-downloader-twitter.jpg" />
                
                {/* Additional SEO meta tags */}
                <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
                <meta name="googlebot" content="index, follow" />
                <meta name="author" content="YT Copycat" />
                <meta name="publisher" content="YT Copycat" />
                <meta name="language" content="en" />
                <meta name="geo.region" content="US" />
                
                {/* Canonical and alternate links */}
                <link rel="canonical" href="https://ytcopycat.com/youtube_thumbnail_downloader" />
                <link rel="alternate" hrefLang="en" href="https://ytcopycat.com/youtube_thumbnail_downloader" />
                
                {/* Schema.org structured data */}
                <script type="application/ld+json">
                    {JSON.stringify(webApplicationSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
            </Helmet>

            <Navbar />
            
            <main className="yt-thumbnail-main">
                <div className="yt-thumbnail-container">
                    <header className="yt-thumbnail-header">
                        <h1>YouTube Thumbnail Downloader</h1>
                        <p>Download high-quality thumbnails from any YouTube video for free. Extract HD thumbnail images instantly - no registration required.</p>
                    </header>

                    <section className="tool-features-compact" aria-label="YouTube thumbnail downloader features">
                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-download" aria-hidden="true"></i>
                            </div>
                            <h3>HD Quality</h3>
                            <p>Download thumbnails in maximum resolution and highest quality available.</p>
                        </article>

                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-bolt" aria-hidden="true"></i>
                            </div>
                            <h3>Instant Access</h3>
                            <p>Get thumbnail images instantly without any waiting time or delays.</p>
                        </article>

                        <article className="tool-feature-item">
                            <div className="icon">
                                <i className="fas fa-gift" aria-hidden="true"></i>
                            </div>
                            <h3>100% Free</h3>
                            <p>No registration, no limits, no hidden fees. Completely free to use.</p>
                        </article>
                    </section>
                    
                    <section className="tool-form-enhanced">
                        <form onSubmit={handleSubmit} role="search" aria-label="YouTube thumbnail download form">
                            <div className="yt-thumbnail-input-group">
                                <label htmlFor="videoUrl">YouTube Video URL</label>
                                <input
                                    type="url"
                                    id="videoUrl"
                                    className="yt-thumbnail-input"
                                    value={videoUrl}
                                    onChange={(e) => setVideoUrl(e.target.value)}
                                    placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                                    required
                                />
                            </div>

                            <button type="submit" className="button-plain">
                                Get Thumbnail
                            </button>
                        </form>

                        {error && <div className="error-message">{error}</div>}

                        {thumbnailUrl && (
                            <div className="yt-thumbnail-result">
                                <h2 className="yt-thumbnail-title">Thumbnail Preview</h2>
                                <img 
                                    src={thumbnailUrl} 
                                    alt="High-quality YouTube video thumbnail preview ready for download" 
                                    className="yt-thumbnail-image"
                                    loading="lazy"
                                />
                                <div className="yt-thumbnail-actions">
                                    <input
                                        type="url"
                                        value={thumbnailUrl}
                                        readOnly
                                        className="yt-thumbnail-input"
                                    />
                                    <div className="yt-thumbnail-buttons">
                                        <button 
                                            onClick={handleDownload} 
                                            className="button-plain"
                                        >
                                            <i className="fas fa-download" aria-hidden="true"></i> Download Thumbnail
                                        </button> 
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    <div className="tool-section-divider"></div>

                    <section className="tool-benefits-section" aria-label="Benefits of YouTube thumbnail downloader">
                        <h2>Why Use Our YouTube Thumbnail Downloader?</h2>
                        <div className="tool-benefits-grid">
                            <article className="tool-benefit-item">
                                <h3>High-Quality Downloads</h3>
                                <p>Download YouTube video thumbnails in maximum resolution for the best image quality.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>No Registration Required</h3>
                                <p>Start downloading thumbnails immediately without creating an account or signing up.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>All URL Formats</h3>
                                <p>Works with youtube.com/watch, youtu.be short links, and mobile URLs.</p>
                            </article>
                            <article className="tool-benefit-item">
                                <h3>Browser-Based Tool</h3>
                                <p>Use directly in your web browser - no software downloads required.</p>
                            </article>
                        </div>
                    </section>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default YTThumbnail; 